import React from 'react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { FiAlertTriangle, FiCheckCircle, FiClock } from 'react-icons/fi';

import { IndicatorCounts, IndicatorKey } from './utils';

interface StatCardProps {
  icon: IconType;
  label: string;
  value: React.ReactNode;
  accent: string;
  isActive?: boolean;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  icon,
  label,
  value,
  accent,
  isActive = false,
  onClick,
}) => {
  // Clicável só quando a tela passa onSelect (lista); no Dashboard é
  // informativo e continua um Box comum.
  const clickableProps = onClick
    ? {
        as: 'button' as const,
        type: 'button' as const,
        onClick,
        'aria-pressed': isActive,
        title: isActive
          ? 'Clique para remover o filtro'
          : 'Clique para filtrar',
        w: '100%',
        textAlign: 'left' as const,
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        _hover: { borderColor: `${accent}.400` },
        _focusVisible: { boxShadow: 'outline', outline: 'none' },
      }
    : {};

  return (
    <Box
      bg={isActive ? 'gray.700' : 'gray.800'}
      borderWidth={isActive ? '2px' : '1px'}
      borderColor={isActive ? `${accent}.400` : 'gray.700'}
      borderRadius="lg"
      p="6"
      {...clickableProps}
    >
      <Flex align="center" mb="3">
        <Flex
          align="center"
          justify="center"
          w="10"
          h="10"
          borderRadius="lg"
          bg={`${accent}.100`}
          color={`${accent}.500`}
          mr="3"
        >
          <Icon as={icon} fontSize="20" />
        </Flex>
        <Text fontSize="sm" color="gray.400" fontWeight="500">
          {label}
        </Text>
      </Flex>
      <Text fontSize="3xl" fontWeight="700" color="gray.50" lineHeight="1.1">
        {value}
      </Text>
    </Box>
  );
};

interface IndicatorCardsProps {
  indicators: Pick<IndicatorCounts, IndicatorKey>;
  active?: IndicatorKey | null;
  onSelect?: (key: IndicatorKey) => void;
}

const CARDS: {
  key: IndicatorKey;
  icon: IconType;
  label: string;
  accent: string;
}[] = [
  { key: 'open', icon: FiClock, label: 'Abertas', accent: 'orange' },
  { key: 'inProgress', icon: FiClock, label: 'Em Andamento', accent: 'blue' },
  { key: 'overdue', icon: FiAlertTriangle, label: 'Vencidas', accent: 'red' },
  { key: 'closed', icon: FiCheckCircle, label: 'Concluídas', accent: 'green' },
];

/**
 * Cards Abertas / Em Andamento / Vencidas / Concluídas. Com `onSelect`, cada
 * card vira um atalho de filtro (usado na lista de Não Conformidades).
 */
export const IndicatorCards: React.FC<IndicatorCardsProps> = ({
  indicators,
  active = null,
  onSelect,
}) => (
  <Box
    display="grid"
    gridTemplateColumns={{
      base: '1fr',
      sm: '1fr 1fr',
      lg: 'repeat(4, 1fr)',
    }}
    gridGap="4"
  >
    {CARDS.map(card => (
      <StatCard
        key={card.key}
        icon={card.icon}
        label={card.label}
        value={indicators[card.key]}
        accent={card.accent}
        isActive={active === card.key}
        onClick={onSelect ? () => onSelect(card.key) : undefined}
      />
    ))}
  </Box>
);
